import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Package, 
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  FileCheck
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, ADMIN_BACKEND_URL } from "./AdminLayout";

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [paymentFilter]);

  const fetchOrders = async () => {
    try {
      let url = `${ADMIN_BACKEND_URL}/api/admin/orders?limit=100`;
      if (paymentFilter && paymentFilter !== "all") {
        url += `&payment_status=${paymentFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await adminFetch(url);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminFetch(`${ADMIN_BACKEND_URL}/api/admin/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchOrders();
  };

  const updateShippingStatus = async (orderId, status) => {
    try {
      const response = await adminFetch(`${ADMIN_BACKEND_URL}/api/admin/orders/${orderId}/shipping-status?status=${status}`, {
        method: 'PATCH'
      });
      
      if (response.ok) {
        toast.success(`Shipping status updated to ${status}`);
        fetchOrders();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      paid: "bg-emerald-100 text-emerald-800",
      pending: "bg-amber-100 text-amber-800",
      expired: "bg-red-100 text-red-800",
      cancelled: "bg-slate-100 text-slate-800"
    };
    return styles[status] || "bg-slate-100 text-slate-800";
  };

  const getShippingStatusBadge = (status) => {
    const styles = {
      pending: "bg-slate-100 text-slate-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-emerald-100 text-emerald-800"
    };
    return styles[status] || "bg-slate-100 text-slate-800";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-orders-headline">
            Order Management
          </h1>
          <p className="text-slate-600">View and manage customer orders</p>
        </div>
      </section>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_orders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Paid Orders</p>
                <p className="text-2xl font-bold text-slate-900">{stats.paid_orders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold text-slate-900">{stats.pending_orders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">${(stats.total_revenue || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by order ID, customer, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="order-search"
              />
            </div>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="payment-filter">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline" className="border-slate-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </form>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No orders found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div 
                key={order.order_id} 
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`order-${order.order_id}`}
              >
                {/* Order Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-mono text-sm text-slate-500">{order.order_id}</p>
                        <p className="font-semibold text-slate-900">{order.customer?.company_name}</p>
                        <p className="text-sm text-slate-600">{order.customer?.full_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">{formatDate(order.created_at)}</p>
                        <p className="text-lg font-bold text-slate-900">${order.total?.toFixed(2)}</p>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusBadge(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getShippingStatusBadge(order.shipping_status)}`}>
                          {order.shipping_status}
                        </span>
                        {order.customer?.tax_certificate_file_id && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid={`cert-badge-${order.order_id}`}>
                            Tax Cert
                          </span>
                        )}
                      </div>
                      
                      {expandedOrder === order.order_id ? 
                        <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      }
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedOrder === order.order_id && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Contact Info */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Contact</h4>
                        <div className="space-y-1 text-sm">
                          <p className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <a href={`mailto:${order.customer?.email}`} className="text-sky-600 hover:underline">
                              {order.customer?.email}
                            </a>
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <a href={`tel:${order.customer?.phone}`} className="text-sky-600 hover:underline">
                              {order.customer?.phone}
                            </a>
                          </p>
                        </div>
                      </div>
                      
                      {/* Shipping Address */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Shipping</h4>
                        <p className="text-sm text-slate-600">
                          {order.customer?.shipping_address}<br />
                          {order.customer?.shipping_city}, {order.customer?.shipping_state} {order.customer?.shipping_zip}
                        </p>
                      </div>
                      
                      {/* Items */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Items</h4>
                        <div className="space-y-1 text-sm">
                          {order.items?.map((item, idx) => (
                            <p key={idx} className="text-slate-600">
                              {item.quantity}x {item.product_name} - ${item.subtotal?.toFixed(2)}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Notes */}
                    {order.customer?.notes && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <strong>Notes:</strong> {order.customer.notes}
                        </p>
                      </div>
                    )}
                    
                    {order.customer?.tax_exempt_id && (
                      <div className="mt-2 text-sm text-sky-600">
                        <strong>Tax Exempt ID:</strong> {order.customer.tax_exempt_id}
                      </div>
                    )}
                    
                    {order.customer?.tax_certificate_file_id && (
                      <div className="mt-2" data-testid={`certificate-indicator-${order.order_id}`}>
                        <a
                          href={`${ADMIN_BACKEND_URL}/api/admin/orders/${order.order_id}/certificate/${order.customer.tax_certificate_file_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 hover:bg-amber-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const token = localStorage.getItem('clearpath_admin_token');
                            const url = `${ADMIN_BACKEND_URL}/api/admin/orders/${order.order_id}/certificate/${order.customer.tax_certificate_file_id}`;
                            fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                              .then(res => res.blob())
                              .then(blob => {
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, '_blank');
                              })
                              .catch(() => toast.error("Failed to download certificate"));
                            e.preventDefault();
                          }}
                          data-testid={`certificate-download-${order.order_id}`}
                        >
                          <FileCheck className="w-4 h-4" />
                          <span>Tax Certificate Uploaded - View</span>
                        </a>
                      </div>
                    )}
                    
                    {/* Shipping Status Update */}
                    {order.payment_status === "paid" && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-medium text-slate-700">Update Shipping:</span>
                          <div className="flex gap-2">
                            {["pending", "processing", "shipped", "delivered"].map((status) => (
                              <Button
                                key={status}
                                size="sm"
                                variant={order.shipping_status === status ? "default" : "outline"}
                                className={order.shipping_status === status ? "bg-sky-600" : "border-slate-300"}
                                onClick={() => updateShippingStatus(order.order_id, status)}
                                data-testid={`shipping-${status}-${order.order_id}`}
                              >
                                {status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                                {status === "processing" && <RefreshCw className="w-3 h-3 mr-1" />}
                                {status === "shipped" && <Truck className="w-3 h-3 mr-1" />}
                                {status === "delivered" && <CheckCircle className="w-3 h-3 mr-1" />}
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
